/**
 * FeedingPatterns Component
 *
 * Displays two charts for visualizing feeding patterns:
 * 1. Scatter plot showing individual feed events by time of day
 * 2. Bar chart showing average volume consumed by hour
 */

import { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  ScatterChart,
  Scatter,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell,
} from 'recharts';
import { format, parseISO, differenceInCalendarDays, subDays, startOfDay } from 'date-fns';
import { eventApi } from '../services/api';
import { getBabyKey, decryptEventData } from '../crypto';

interface FeedingPatternsProps {
  babyId: string;
}

interface ScatterPoint {
  timeOfDay: number; // minutes since midnight (0-1439)
  dayIndex: number;
  date: string;
  time: string;
  amount: number | null;
  feedCategory: 'breast' | 'formula';
}

interface HourlyBucket {
  hour: number;
  hourLabel: string;
  totalMl: number;
  feedCount: number;
  averageMl: number;
}

interface DataEvent {
  id: string;
  timestamp: string;
  encryptedData: string;
}

const PERIODS = [7, 14, 30] as const;
type Period = typeof PERIODS[number];

function formatHourLabel(hour: number): string {
  if (hour === 0) return '12AM';
  if (hour < 12) return `${hour}AM`;
  if (hour === 12) return '12PM';
  return `${hour - 12}PM`;
}

export default function FeedingPatterns({ babyId }: FeedingPatternsProps) {
  const [period, setPeriod] = useState<Period>(7);

  const startDate = useMemo(() => {
    return startOfDay(subDays(new Date(), period)).toISOString();
  }, [period]);

  // Fetch FEED events
  const { data: feedEventsRaw, isLoading: feedLoading } = useQuery({
    queryKey: ['feeding-patterns', babyId, 'FEED', period],
    queryFn: async (): Promise<DataEvent[]> => {
      const response = await eventApi.list(babyId, {
        type: 'FEED',
        startDate,
        limit: '1000',
      });
      return (response.data?.events as DataEvent[]) ?? [];
    },
    staleTime: 5 * 60 * 1000,
  });

  // Fetch BOTTLE events
  const { data: bottleEventsRaw, isLoading: bottleLoading } = useQuery({
    queryKey: ['feeding-patterns', babyId, 'BOTTLE', period],
    queryFn: async (): Promise<DataEvent[]> => {
      const response = await eventApi.list(babyId, {
        type: 'BOTTLE',
        startDate,
        limit: '1000',
      });
      return (response.data?.events as DataEvent[]) ?? [];
    },
    staleTime: 5 * 60 * 1000,
  });

  // Decrypt and transform chart data
  const { data: chartData, isLoading: chartLoading } = useQuery({
    queryKey: ['feeding-patterns-chart', babyId, period, feedEventsRaw, bottleEventsRaw],
    queryFn: async () => {
      const allEvents = [
        ...(feedEventsRaw || []).map((e) => ({ ...e, source: 'FEED' as const })),
        ...(bottleEventsRaw || []).map((e) => ({ ...e, source: 'BOTTLE' as const })),
      ];

      if (allEvents.length === 0) {
        return { scatterPoints: [], hourlyData: [], totalDays: period };
      }

      const babyKey = await getBabyKey(babyId);
      if (!babyKey) return { scatterPoints: [], hourlyData: [], totalDays: period };

      const periodStart = startOfDay(subDays(new Date(), period));
      const scatterPoints: ScatterPoint[] = [];
      const hourlyBuckets: Map<number, { totalMl: number; count: number }> = new Map();

      // Initialize hourly buckets
      for (let h = 0; h < 24; h++) {
        hourlyBuckets.set(h, { totalMl: 0, count: 0 });
      }

      for (const event of allEvents) {
        try {
          const decrypted = await decryptEventData(event.encryptedData, babyKey);
          if (!decrypted) continue;

          const timestamp = parseISO(event.timestamp);
          const hours = timestamp.getHours();
          const minutes = timestamp.getMinutes();
          const timeOfDay = hours * 60 + minutes;
          const dayIndex = differenceInCalendarDays(timestamp, periodStart);

          // Determine amount
          let amount: number | null = null;
          if (event.source === 'BOTTLE') {
            amount = (decrypted.amountConsumed as number) ?? (decrypted.amountOffered as number) ?? null;
          } else {
            amount = (decrypted.amount as number) ?? null;
          }

          let feedCategory: 'breast' | 'formula';
          if (event.source === 'BOTTLE') {
            feedCategory = 'formula';
          } else {
            // Check decrypted feedType to correctly categorize formula/both FEED events
            const ft = decrypted.feedType as string | undefined;
            feedCategory = (ft === 'formula' || ft === 'both') ? 'formula' : 'breast';
          }

          scatterPoints.push({
            timeOfDay,
            dayIndex,
            date: format(timestamp, 'MMM d'),
            time: format(timestamp, 'h:mm a'),
            amount,
            feedCategory,
          });

          // Accumulate hourly data
          const bucket = hourlyBuckets.get(hours)!;
          bucket.count++;
          if (amount !== null) {
            bucket.totalMl += amount;
          }
        } catch {
          // Skip events that fail to decrypt
        }
      }

      const hourlyData: HourlyBucket[] = [];
      for (let h = 0; h < 24; h++) {
        const bucket = hourlyBuckets.get(h)!;
        hourlyData.push({
          hour: h,
          hourLabel: formatHourLabel(h),
          totalMl: bucket.totalMl,
          feedCount: bucket.count,
          averageMl: bucket.count > 0 ? bucket.totalMl / bucket.count : 0,
        });
      }

      return { scatterPoints, hourlyData, totalDays: period };
    },
    enabled: !feedLoading && !bottleLoading,
    staleTime: 5 * 60 * 1000,
  });

  const isLoading = feedLoading || bottleLoading || chartLoading;
  const scatterPoints = chartData?.scatterPoints ?? [];
  const hourlyData = chartData?.hourlyData ?? [];
  const totalDays = chartData?.totalDays ?? period;

  const breastPoints = scatterPoints.filter((p) => p.feedCategory === 'breast');
  const formulaPoints = scatterPoints.filter((p) => p.feedCategory === 'formula');

  const hasVolumeData = hourlyData.some((h) => h.totalMl > 0);

  // Dynamic scatter chart height
  const scatterHeight = Math.max(200, totalDays * 18 + 60);

  // Dot size based on amount
  const getDotSize = (amount: number | null): number => {
    if (amount === null || amount === 0) return 6;
    // Scale between 4px and 12px based on typical range 0-300ml
    return Math.min(12, Math.max(4, 4 + (amount / 300) * 8));
  };

  // Custom scatter tooltip
  const ScatterTooltipContent = ({ active, payload }: any) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload as ScatterPoint;
      return (
        <div className="bg-white dark:bg-gray-800 p-3 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700">
          <p className="font-semibold text-gray-900 dark:text-white">{data.date}</p>
          <p className="text-sm text-gray-600 dark:text-gray-300">{data.time}</p>
          <p className="text-sm" style={{ color: data.feedCategory === 'breast' ? '#06b6d4' : '#a855f7' }}>
            {data.feedCategory === 'breast' ? 'Breast' : 'Formula'}
          </p>
          {data.amount !== null && (
            <p className="text-sm text-gray-600 dark:text-gray-300">{data.amount} ml</p>
          )}
        </div>
      );
    }
    return null;
  };

  // Custom bar tooltip
  const BarTooltipContent = ({ active, payload }: any) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload as HourlyBucket;
      return (
        <div className="bg-white dark:bg-gray-800 p-3 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700">
          <p className="font-semibold text-gray-900 dark:text-white">{data.hourLabel}</p>
          <p className="text-sm text-gray-600 dark:text-gray-300">
            Avg: {data.averageMl.toFixed(0)} ml
          </p>
          <p className="text-sm text-gray-600 dark:text-gray-300">
            Feeds: {data.feedCount}
          </p>
          {data.totalMl > 0 && (
            <p className="text-sm text-gray-600 dark:text-gray-300">
              Total: {data.totalMl.toFixed(0)} ml
            </p>
          )}
        </div>
      );
    }
    return null;
  };

  // X-axis tick formatter for scatter (time of day)
  const timeTickFormatter = (value: number) => {
    const h = Math.floor(value / 60);
    return formatHourLabel(h);
  };

  // Y-axis tick formatter for scatter (date)
  const dateTickFormatter = (value: number) => {
    const date = subDays(new Date(), period - value);
    return format(date, 'MMM d');
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl p-4">
      {/* Header with period selector */}
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
          Feeding Patterns
        </h3>
        <div className="flex gap-1">
          {PERIODS.map((p) => (
            <button
              key={p}
              onClick={() => setPeriod(p)}
              className={`px-3 py-1 text-sm rounded-lg font-medium transition-colors ${
                period === p
                  ? 'bg-primary-600 text-white'
                  : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
              }`}
            >
              {p}d
            </button>
          ))}
        </div>
      </div>

      {isLoading ? (
        <div className="py-12 text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500 mx-auto"></div>
          <p className="mt-3 text-sm text-gray-500 dark:text-gray-400">Loading feeding data...</p>
        </div>
      ) : scatterPoints.length === 0 ? (
        <div className="py-12 text-center">
          <p className="text-gray-500 dark:text-gray-400">
            No feeding data for the last {period} days.
          </p>
        </div>
      ) : (
        <>
          {/* Chart 1: Feed Times Scatter */}
          <div className="mb-6">
            <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Feed Times
            </h4>

            {/* Legend */}
            <div className="flex gap-4 mb-2 text-xs text-gray-500 dark:text-gray-400">
              {breastPoints.length > 0 && (
                <span className="flex items-center gap-1">
                  <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: '#06b6d4' }}></span>
                  Breast
                </span>
              )}
              {formulaPoints.length > 0 && (
                <span className="flex items-center gap-1">
                  <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: '#a855f7' }}></span>
                  Formula
                </span>
              )}
              <span className="text-gray-400 dark:text-gray-500">Dot size = volume</span>
            </div>

            <div style={{ height: scatterHeight }}>
              <ResponsiveContainer width="100%" height="100%">
                <ScatterChart margin={{ top: 5, right: 10, left: 10, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" className="stroke-gray-200 dark:stroke-gray-700" />
                  <XAxis
                    type="number"
                    dataKey="timeOfDay"
                    domain={[0, 1440]}
                    ticks={[0, 180, 360, 540, 720, 900, 1080, 1260]}
                    tickFormatter={timeTickFormatter}
                    className="text-gray-600 dark:text-gray-400"
                    fontSize={11}
                  />
                  <YAxis
                    type="number"
                    dataKey="dayIndex"
                    domain={[0, period]}
                    reversed
                    tickFormatter={dateTickFormatter}
                    tickCount={Math.min(period, 8)}
                    className="text-gray-600 dark:text-gray-400"
                    fontSize={11}
                    width={45}
                  />
                  <Tooltip content={<ScatterTooltipContent />} />

                  {/* Breast feeds */}
                  {breastPoints.length > 0 && (
                    <Scatter name="Breast" data={breastPoints}>
                      {breastPoints.map((point, index) => (
                        <Cell
                          key={`breast-${index}`}
                          fill="#06b6d4"
                          r={getDotSize(point.amount)}
                        />
                      ))}
                    </Scatter>
                  )}

                  {/* Formula feeds */}
                  {formulaPoints.length > 0 && (
                    <Scatter name="Formula" data={formulaPoints}>
                      {formulaPoints.map((point, index) => (
                        <Cell
                          key={`formula-${index}`}
                          fill="#a855f7"
                          r={getDotSize(point.amount)}
                        />
                      ))}
                    </Scatter>
                  )}
                </ScatterChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Chart 2: Average Volume by Hour */}
          <div>
            <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Average Volume by Hour
            </h4>

            {!hasVolumeData && (
              <p className="text-xs text-gray-400 dark:text-gray-500 mb-2">
                No volume data logged. Log ml amounts to see hourly averages.
              </p>
            )}

            <div style={{ height: 250 }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={hourlyData} margin={{ top: 5, right: 10, left: 10, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" className="stroke-gray-200 dark:stroke-gray-700" />
                  <XAxis
                    dataKey="hourLabel"
                    interval={2}
                    className="text-gray-600 dark:text-gray-400"
                    fontSize={11}
                  />
                  <YAxis
                    className="text-gray-600 dark:text-gray-400"
                    fontSize={11}
                    width={40}
                    label={{
                      value: 'ml',
                      angle: -90,
                      position: 'insideLeft',
                      style: { textAnchor: 'middle' },
                    }}
                  />
                  <Tooltip content={<BarTooltipContent />} />
                  <Bar dataKey="averageMl" fill="#a855f7" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
